import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bot, Compass, Loader2, MessageCircle, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { sharedSupabase } from "../lib/supabase-client";
import { getSiteProfile, isNaviHost } from "../lib/site-profile";

const siteProfile = getSiteProfile();

const defaultConfig = {
  assistantEnabled: true,
  assistantName: siteProfile.assistantName,
  menuLabel: "Assistant",
  welcomeMessage: siteProfile.assistantWelcomeMessage,
  primaryCtaLabel: "Explore Discovery",
  primaryCtaHref: "/discover",
};

export default function AssistantPage() {
  const standaloneNavi = isNaviHost();
  const [config, setConfig] = useState(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (standaloneNavi) {
      setLoading(false);
      setConfig(defaultConfig);
      setStatus("CaribNavi is using its standalone assistant profile.");
      return undefined;
    }

    const load = async () => {
      if (!sharedSupabase) {
        setStatus("Assistant settings are unavailable right now.");
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await sharedSupabase
          .from("navi_settings")
          .select("*")
          .order("updated_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        if (data) {
          const nested = data.settings && typeof data.settings === "object" ? data.settings : null;
          const read = (key) => data[key] ?? (nested ? nested[key] : undefined);
          setConfig({
            assistantEnabled:
              typeof read("assistantEnabled") === "boolean"
                ? read("assistantEnabled")
                : defaultConfig.assistantEnabled,
            assistantName:
              typeof read("assistantName") === "string"
                ? read("assistantName")
                : defaultConfig.assistantName,
            menuLabel:
              typeof read("menuLabel") === "string" ? read("menuLabel") : defaultConfig.menuLabel,
            welcomeMessage:
              typeof read("welcomeMessage") === "string"
                ? read("welcomeMessage")
                : defaultConfig.welcomeMessage,
            primaryCtaLabel:
              typeof read("primaryCtaLabel") === "string"
                ? read("primaryCtaLabel")
                : defaultConfig.primaryCtaLabel,
            primaryCtaHref:
              typeof read("primaryCtaHref") === "string"
                ? read("primaryCtaHref")
                : defaultConfig.primaryCtaHref,
          });
        }
      } catch (err) {
        setStatus("Unable to fetch Assistant settings. Please retry later.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [standaloneNavi]);

  const isEnabled = useMemo(() => Boolean(config.assistantEnabled), [config.assistantEnabled]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <main className="max-w-4xl mx-auto px-4 md:px-8 py-16">
        <div className="rounded-3xl border border-cyan-100 bg-white shadow-lg p-6 md:p-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500 to-orange-500 flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-slate-900">{config.assistantName}</h1>
              <p className="text-sm text-slate-500">{siteProfile.assistantSourceLabel}</p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 py-10">
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading assistant configuration…
            </div>
          ) : (
            <div className="space-y-6">
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-cyan-600" />
                  <span className="text-sm font-semibold text-slate-700">Assistant Status</span>
                </div>
                <p className={`font-medium ${isEnabled ? "text-emerald-600" : "text-amber-600"}`}>
                  {isEnabled
                    ? "Assistant is online."
                    : standaloneNavi
                      ? "Assistant is temporarily paused for Navi."
                      : "Assistant is temporarily paused. Please check back shortly."}
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
                <div className="flex items-center gap-2 mb-2">
                  <MessageCircle className="w-4 h-4 text-cyan-600" />
                  <span className="text-sm font-semibold text-slate-700">Welcome Message</span>
                </div>
                <p className="text-slate-700">{config.welcomeMessage}</p>
              </div>

              {status && <p className="text-sm text-amber-600">{status}</p>}

              <div className="flex flex-wrap gap-3">
                <Link to={config.primaryCtaHref || "/discover"}>
                  <Button className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white rounded-full">
                    <Compass className="w-4 h-4 mr-2" />
                    {config.primaryCtaLabel || "Explore Discovery"}
                  </Button>
                </Link>
                <Link to="/discover">
                  <Button variant="outline" className="rounded-full">
                    Continue Exploring
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
